import { Component, OnInit, OnDestroy, Injectable } from '@angular/core';
import { DateTime } from '@lib/date-time';
import { ActivatedRoute, Router } from '@angular/router';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { BehaviorSubject, Observable, Subject, combineLatest, firstValueFrom, map, take, takeUntil } from 'rxjs';
import { NavigationRoute$, User$ } from '@injectables';
import { op } from '@lib/rxjs';
import { combineInput } from '@app/new-ui/analysis/analysis.component';
import { generateTimelineChartLines } from '@app/new-ui/chart-transforms';
import { VehicleQueryService } from 'src/injectables/vehicle-query.service';
import { DateAdapter } from '@angular/material/core';
import { MatDateRangeSelectionStrategy, DateRange, MAT_DATE_RANGE_SELECTION_STRATEGY } from '@angular/material/datepicker';
import { Device, Site } from '@models';
import { DateRangeService } from '../../../utilities/date-range.service';
import { distinctUntilChanged, switchMap, filter, skip } from 'rxjs/operators';
import { asFiniteNumber, isValidNumber } from '@lib/number';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { getUserIANATimezone } from '@lib/timezone-preference-list';
@Component({
    selector: 'filters',
    templateUrl: './filters.component.html',
    styleUrls: ['./filters.component.scss'],
    providers: [
        {
            provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
            useClass: FiltersComponent,
        },
    ],
})
export class FiltersComponent<D extends Date> implements OnInit, OnDestroy, MatDateRangeSelectionStrategy<D> {
    public defaultDateRange: DateRange<D>;
    public readonly sites$: Observable<Site[]> = this.fragQuery.sites$;
    public readonly selectedSiteId$: BehaviorSubject<number | null> = this.fragQuery.siteId$;

    constructor(
        public readonly fragQuery: FragQueryService,
        private readonly vehicleQuery: VehicleQueryService,
        private readonly route: ActivatedRoute,
        private readonly route$: NavigationRoute$,
        public readonly user$: User$,
        private readonly _dateAdapter: DateAdapter<D>,
        private dateRangeService: DateRangeService<D>
    ) { }

    public xHighlight: DateTime.Range = null;
    public readonly durationOptions = this.fragQuery.durationOptions;
    public readonly deviceFlowDurationOptions = this.durationOptions.pipe(
        map(options => options.filter(option => option.type !== 'shifts'))
    );
    public readonly vehicleQueryDurationOptions = this.vehicleQuery.durationOptions;
    public readonly duration$ = this.fragQuery.duration$;
    public readonly newPercents$ = this.fragQuery.newPercents$;
    public readonly devices$ = this.fragQuery.devices$;
    public readonly device$ = this.fragQuery.device$;
    public readonly shifts$ = this.fragQuery.shifts$;
    public readonly shift$ = this.fragQuery.shift$;
    public readonly begin$ = this.fragQuery.begin$;
    public readonly end$ = this.fragQuery.end$;
    public readonly customShiftDate$ = this.fragQuery.customShiftDate$;
    public readonly currentInterval = new BehaviorSubject(null);
    public readonly chartType$ = this.fragQuery.chartType$;
    public readonly weightType$ = this.fragQuery.weightType$;
    public readonly graph$ = this.fragQuery.graph$;
    public readonly particleSizeUnit$ = this.fragQuery.particleSizeUnit$;
    public readonly muckpiles$ = this.fragQuery.muckpiles$;
    public readonly muckpile$ = this.fragQuery.muckpile$;
    public readonly deviceUuid$ = this.fragQuery.deviceUuid$
    public readonly muckpileId$ = this.fragQuery.muckpileId$
     
    // vehicle query filters
    public readonly vehicleQueryDuration$ = this.vehicleQuery.duration$;
    public tempBegin: DateTime | null = null;
    public tempEnd: DateTime | null = null;
    private pickerTimezone: string | undefined;
    private readonly destroy$ = new Subject<void>();

    // Compare calendar days against "now" in the same timezone as the custom drafts.
    public readonly customDateFilter = (date: DateTime): boolean =>
        date <= this.toPickerDate(new DateTime(Date.now()));

    public ngOnInit(): void {
        const fragment = JSON.parse(this.route.snapshot.fragment);
        if (fragment?.custom) {
            this.fragQuery.duration$.next('custom');
            if (Array.isArray(fragment.custom)) {
                this.fragQuery.begin$.next(
                    new DateTime(fragment.custom[0] ?? DateTime.now())
                );
                this.fragQuery.end$.next(
                    new DateTime(fragment.custom[1] ?? DateTime.now())
                );
            }
        }
        if (fragment?.device) {
            this.fragQuery.devices$.pipe(take(1)).subscribe(devices => {
                const num = asFiniteNumber(fragment.device);
                const found = devices.find(d => d.id === num || d.uuid === fragment.device);
                if (found) {
                    this.fragQuery.deviceUuid$.next(found.uuid);
                }
            });
        }
        //If fragment contains a site id, set it
        if (fragment?.site) {
            const siteIdNum = asFiniteNumber(fragment.site);
            if (isValidNumber(siteIdNum)) {
                this.fragQuery.siteId$.next(siteIdNum);
            }
        }

        combineLatest([this.duration$, this.user$]).pipe(
            takeUntil(this.destroy$)
        ).subscribe(([duration, user]) => {
            if (!user) return;

            this.pickerTimezone = user.timezone && user.timezone !== 'Default'
                ? getUserIANATimezone(user.timezone)
                : undefined;

            if (duration === 'week') {
                void this.setDefaultDateRange();
            }

            if (duration === 'custom') {
                // Pickers edit local Date fields; represent the profile's wall time here.
                this.tempBegin = this.toPickerDate(this.begin$.value);
                this.tempEnd = this.toPickerDate(this.end$.value);
            }
        });

        this.fragQuery.siteId$.pipe(
            distinctUntilChanged(),
            switchMap((_) => this.fragQuery.devices$.pipe(take(1)))
        ).subscribe(devices => {
            const uuid = devices?.[0]?.uuid ?? null;
            this.fragQuery.deviceUuid$.next(uuid);
        });
    }

    private async setDefaultDateRange(): Promise<void> {
        const today = this._dateAdapter.today();
        const weekRange = this.dateRangeService.createWeekRange(today);
        if (weekRange.start && weekRange.end) {
            this.defaultDateRange = new DateRange(weekRange.start, weekRange.end);
            this.dateRangeService.onStartDateChange(weekRange.start);
            this.dateRangeService.onEndDateChange(weekRange.end);
        }
    }

    public handleMuckpileChnage(id: string) {
        this.muckpiles$.pipe(
            take(1),
            map((muckpiles) => muckpiles.find((m) =>
                m.id === asFiniteNumber(id))))
            .subscribe((muckpile) => {
                this.fragQuery.muckpile$.next(muckpile);
            });
    }

    public fetch() {
        if (!this.tempBegin?.valid() || !this.tempEnd?.valid()) return;
        // Convert both drafts before publishing either value. Keep the drafts in wall time
        // so repeated Fetch clicks do not apply the timezone conversion again.
        const begin = this.fromPickerDate(this.tempBegin);
        const end = this.fromPickerDate(this.tempEnd);
        if (!begin.valid() || !end.valid() || end < begin) return;
        this.begin$.next(begin);
        this.end$.next(end);
    }

    private toPickerDate(value: DateTime): DateTime {
        return new DateTime(this.pickerTimezone ? toZonedTime(value, this.pickerTimezone) : value);
    }

    private fromPickerDate(value: DateTime): DateTime {
        return new DateTime(this.pickerTimezone ? fromZonedTime(value, this.pickerTimezone) : value);
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public selectionFinished(date: D | null): DateRange<D> {
        return this.dateRangeService.selectionFinished(date)
    }

    public createPreview(activeDate: D | null): DateRange<D> {
        return this.dateRangeService.createWeekRange(activeDate);
    }

    public readonly chartLines$: Observable<Highcharts.Options> = combineLatest([
        this.fragQuery.newPercents$,
        this.fragQuery.timelineSieveSizesAtPercents$,
        this.fragQuery.addOnTimelineSieveSizesAtPercents$,
        this.fragQuery.particleSizeUnit$,
    ]).pipe(
        op.tap(([percents, input, addOn]) => combineInput(input, addOn, this.fragQuery.addDataAt.value)),
        op.tap(input => {
            if (input[1] && input[1].intervals) {
                this.fragQuery.totalIntervalLength.next((input[1].end.valueOf() - input[1].begin.valueOf()))
                this.fragQuery.intervalsLength.next(input[1].intervals.length)
            }
        }),
        op.map(([percents, input, input2, unit]) => {
            const timelineChartConfig = {
                chartType: 'line' as const,
                percents,
                input,
                particleSizeUnit: unit,
                zoomingEnable: true,
                navigatorEnable: true,
                tooltipEnable: true,
                scrollbarEnable: true,
                addData: this.addData.bind(this),
                plotClick: this.onPlotClick.bind(this),
            }
            return generateTimelineChartLines(timelineChartConfig);
        })
    )

    addData(type: string) {
        this.fragQuery.addDataAt.next(type);
    }

    async onPlotClick(time: number) {
        const input = await firstValueFrom(
            this.fragQuery.timelineSieveSizesAtPercents$
        );
        let dataPoint;
        for (let index = 0; index < input.intervals.length; index++) {
            const interval = input.intervals[index];
            if (interval.begin.valueOf() <= time) {
                dataPoint = interval;
            } else {
                break;
            }
        }
        if (dataPoint) { this.currentInterval.next(dataPoint) };
    }

    isSmallScreen(): boolean {
        return window.innerWidth <= 1000;
    }

    public readonly type$ = this.route$
        .pipe(op.map(route => route[0]));
                
}
